import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {Translate, I18n} from 'react-redux-i18n';

import {Icon, stringUtils} from 'front-onceforall-core';
import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';
import {useStateHook} from 'front-onceforall-core/dist/utils/customHooks';

import {DOC_TYPE, AUTO_GENERATE_FILE_DOCUMENT_TYPE} from '../../../utils/constant';
import {getDocumentName} from '../../../utils/document';
import {interventionPropTypes} from '../../../utils/propTypes';
import {pathDocumentTemplates} from '../../../utils/path';
import documentService from '../../../services/document/DocumentService';
import {addError, addSuccess} from '../../../actions/snackbars';

import UploadDocumentPanel from './UploadDocumentPanel';
import DownloadAndUploadDocumentPanel from './DownloadAndUploadDocumentPanel';
import DownloadAndUploadForeignEmployeesDocumentPanel from './DownloadAndUploadForeignEmployeesDocumentPanel';

function UploadPanel(props) {
  const [state, setState] = useStateHook({
    templateFile: '',
    isUploadPanelOpen: false,
    isUploadExcelPanelOpen: false,
    isUploadTemplatePanelOpen: false,
    documentToUpload: {},
    submitting: false
  });
  const dispatch = useDispatch();

  const handleDocumentUpload = document => {
    if (document.type.code === DOC_TYPE.FOREIGN_EMPLOYEES) {
      setState({
        documentToUpload: document,
        isUploadExcelPanelOpen: true
      });
      return;
    }

    if (document.type.templatePresent) {
      const file = getDocumentName(document.type.name);
      setState({
        templateFile: `${pathDocumentTemplates}${file}.pdf`,
        documentToUpload: document,
        isUploadTemplatePanelOpen: true
      });
      return;
    }

    if (document.type.code === AUTO_GENERATE_FILE_DOCUMENT_TYPE) {
      const info = {
        additionalDesignation: document.additionalDesignation,
        documentTypeCode: document.type.code
      };
      documentService.submitNewDocument(document.uid, null, info)
        .then(() => {
          props.refreshDocuments();
        })
        .catch(() => {
          dispatch(addError(<Translate value="error.generic"/>));
        });
      return;
    }

    setState({
      documentToUpload: document,
      isUploadPanelOpen: true
    });
  };

  const closeUploadPanels = () => {
    setState({
      isUploadPanelOpen: false,
      isUploadExcelPanelOpen: false,
      isUploadTemplatePanelOpen: false
    });
  };

  const handleUploadSuccess = () => {
    closeUploadPanels();
    props.onUploadSuccess();
  };

  const handleSubmit = (documentId, file, info) => {
    if (state.submitting) {
      return;
    }

    setState({submitting: true});

    documentService.submitNewDocument(documentId, file, info)
      .then(() => {
        if (props.displaySuccessNotification) {
          dispatch(addSuccess(<Translate value="document.uploadSuccess"/>));
        }
        handleUploadSuccess();
      })
      .catch(() => {
        dispatch(addError(<Translate value="error.generic"/>));

        closeUploadPanels();
      })
      .finally(() => {
        setState({submitting: false});
      });
  };

  if (props.setUploadTrigger) {
    props.setUploadTrigger(handleDocumentUpload);
  }

  const uploadPanelMessage = () => (
    <div className="box info p-4 mt-3 flex-container">
      <Icon
        icon="info"
        size={16}
      />
      <div>
        {stringUtils.getStrongs(I18n.t(props.intervention.interventionDate ? 'project.documentAtInterventionDateMessage' : 'project.documentAtDateMessage', {
          date: I18n.l(props.myIntervention.project.openingDate, {dateFormat: 'datetime.shortDate'}),
          interventionDate: I18n.l(props.intervention.interventionDate, {dateFormat: 'datetime.shortDate'})
        }))}
      </div>
    </div>
  );

  return (<>
    {state.isUploadPanelOpen &&
      <UploadDocumentPanel
        onClose={closeUploadPanels}
        onSubmit={handleSubmit}
        document={state.documentToUpload}
        organisationId={props.organisationId}
        message={props.showUploadPanelMessage && !isEmpty(state.documentToUpload.projectContext) && uploadPanelMessage()}
        submitting={state.submitting}
      />
    }
    {state.isUploadExcelPanelOpen &&
      <DownloadAndUploadForeignEmployeesDocumentPanel
        onClose={closeUploadPanels}
        onSubmit={handleSubmit}
        document={state.documentToUpload}
        submitting={state.submitting}
      />
    }
    {state.isUploadTemplatePanelOpen &&
      <DownloadAndUploadDocumentPanel
        documentTemplate={state.templateFile}
        onClose={closeUploadPanels}
        onSubmit={handleSubmit}
        document={state.documentToUpload}
        submitting={state.submitting}
      />
    }
  </>);
}

UploadPanel.defaultProps = {
  showUploadPanelMessage: false,
  displaySuccessNotification: true
};

UploadPanel.propTypes = {
  organisationId: PropTypes.number.isRequired,
  setUploadTrigger: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
  refreshDocuments: PropTypes.func.isRequired,
  showUploadPanelMessage: PropTypes.bool,
  intervention: interventionPropTypes(),
  myIntervention: interventionPropTypes(),
  displaySuccessNotification: PropTypes.bool
};

export default UploadPanel;
